import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {formatDateDMYHM, formatFullDMY, formatLocalISODateToDMY} from "crqlar-react-common/dist/utils/date.util";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useService, useServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {
    birthdayToAge,
    getProfileAllergiesString,
    getProfileFoodDietsString,
    getProfileFullName,
    getProfileMedicalString
} from "crqlar-react-common/dist/utils/profile.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useMemo} from "react";
import {useQuery} from "react-query";
import {useHref} from "react-router-dom";

const SUBMISSION_PROFILE_QUERY_KEY = 'surveySubmissionProfile';
const SUBMISSION_RESERVATION_QUERY_KEY = 'surveySubmissionReservation';

const LOOKUP_STALE_TIME = 5 * 60 * 1000;

/** Enough to show what is already booked without turning the panel into a stay list. */
const UPCOMING_STAY_LIMIT = 2;

export type SurveySubmissionGuestContextKind = 'reservation' | 'context' | 'stay' | 'upcoming';

export type SurveySubmissionGuestContextRow = {
    kind: SurveySubmissionGuestContextKind
    href?: string
    label: string
    title: string
    detail?: string
};

export type SurveySubmissionGuestPreferences = {
    allergies: string[]
    diets: string[]
    medical: string[]
};

export type SurveySubmissionGuest = {
    /** Initial profile/reservation lookup only; cached details stay visible during refreshes. */
    loading: boolean
    name: string
    initials: string
    /** Matched guest categories, completed stays, age, invited vs public link. */
    chips: string[]
    preferences?: SurveySubmissionGuestPreferences
    contextRows: SurveySubmissionGuestContextRow[]
    profileHref?: string
    /** The looked-up records, so the panel can open the profile and reservation dialogs in place. */
    profile?: Profile
    reservation?: Reservation
};

// Hotel stays are whole days; times only mean something for restaurant, spa and planner bookings.
const formatReservationRange = (start?: Date, end?: Date, datesOnly?: boolean) => {
    const format = datesOnly ? formatFullDMY : formatDateDMYHM;
    const formattedStart = format(start);
    const formattedEnd = format(end);
    return [formattedStart, formattedEnd].filter(Boolean).join(' – ');
};

/**
 * The guest panel and every submission export need the same profile and reservation lookup,
 * so it lives here rather than inside the card that first needed it.
 */
export const useSurveySubmissionGuest = (
    submission: AggregationSurveySubmissionListItem,
    enabled: boolean
): SurveySubmissionGuest => {
    const {t, localize} = useLocalize();

    const profileId = submission.profileId;
    const reservationId = submission.reservationId;

    const profileQuery = useQuery(
        [SUBMISSION_PROFILE_QUERY_KEY, profileId],
        () => ProfileService.getProfileById(profileId!, true),
        {enabled: enabled && !!profileId, refetchOnWindowFocus: false, staleTime: LOOKUP_STALE_TIME}
    );

    const reservationQuery = useQuery(
        [SUBMISSION_RESERVATION_QUERY_KEY, reservationId],
        () => ReservationService.getReservationById(reservationId!),
        {enabled: enabled && !!reservationId, refetchOnWindowFocus: false, staleTime: LOOKUP_STALE_TIME}
    );

    const profile = profileQuery.data;
    const reservation = reservationQuery.data;
    const bookedService = useService(reservation?.serviceId);
    const bookedSector = useSector(reservation?.sectorId);

    const lastAccommodationStay = useMemo(
        () => [...(profile?.pastStays ?? [])]
            .sort((a, b) => (
                b.endDay ?? b.startDay ?? ''
            ).localeCompare(a.endDay ?? a.startDay ?? ''))[0],
        [profile?.pastStays]
    );
    const lastStayService = useService(lastAccommodationStay?.accommodationServiceId);

    /*
        What the guest already has booked ahead is the reason a reviewer opens this panel at all:
        the submission's own reservation is skipped, since it is shown as the booked row.
     */
    const upcomingStays = useMemo(
        () => [...(profile?.upcomingStays ?? [])]
            .filter(stay => !!stay.reservationId && stay.reservationId !== reservationId)
            .sort((a, b) => (a.startDay ?? '').localeCompare(b.startDay ?? ''))
            .slice(0, UPCOMING_STAY_LIMIT),
        [profile?.upcomingStays, reservationId]
    );
    const upcomingStayServices = useServices(
        upcomingStays.flatMap(stay => (
            stay.accommodationServiceId ? [stay.accommodationServiceId] : []
        ))
    );

    const profileHref = useHref(`/system/profiles/${profileId ?? ''}`);
    const reservationsBaseHref = useHref('/system/reservations');
    const reservationHref = `${reservationsBaseHref}/${reservationId ?? ''}`;
    const lastStayHref = `${reservationsBaseHref}/${lastAccommodationStay?.reservationId ?? ''}`;

    const name = submission.profileName?.trim()
        || getProfileFullName(profile, true, '')
        || t('Anonymous guest');

    const initials = [profile?.firstName, profile?.lastName]
        .map(part => part?.trim().charAt(0) ?? '')
        .join('')
        .toUpperCase()
        || (submission.profileName?.trim().charAt(0).toUpperCase() ?? '');

    const completedStays = profile
        ? profile.completedStaysNo ?? profile.smartData?.stays?.totalStays ?? profile.allStaysNo
        : undefined;

    const calculatedGuestAge = profile?.birthDay ? birthdayToAge(profile.birthDay) : undefined;
    const guestAge = calculatedGuestAge !== undefined
        && Number.isFinite(calculatedGuestAge)
        && calculatedGuestAge >= 0
        ? calculatedGuestAge
        : undefined;

    const allergies = getProfileAllergiesString(profile);
    const diets = getProfileFoodDietsString(profile);
    const medical = getProfileMedicalString(profile);
    const hasRelevantPreferences = allergies.length > 0 || diets.length > 0 || medical.length > 0;

    const lastStayMatchesBookedReservation = !!reservationId
        && lastAccommodationStay?.reservationId === reservationId;

    const bookedServiceName = localize(bookedService?.nameLocalized)
        || bookedService?.name
        || submission.reservationLabel?.trim()
        || t('Reservation');
    const bookedRange = formatReservationRange(
        reservation?.expectedStartDate ?? reservation?.actualStartDate,
        reservation?.expectedEndDate ?? reservation?.actualEndDate,
        bookedSector?.sectorType === SectorType.ACCOMMODATION
    );
    const bookedDetail = [submission.sectorName, bookedRange].filter(Boolean).join(' · ');

    const lastStayServiceName = localize(lastStayService?.nameLocalized) || lastStayService?.name;
    const lastStayTitle = lastAccommodationStay?.roomId
        ? `${t('Room')} ${lastAccommodationStay.roomId}`
        : lastStayServiceName || t('Stay');
    const lastStayRange = [
        formatLocalISODateToDMY(lastAccommodationStay?.startDay),
        formatLocalISODateToDMY(lastAccommodationStay?.endDay),
    ].filter(Boolean).join(' – ');
    const lastStayDetail = [
        lastStayServiceName && lastStayServiceName !== lastStayTitle ? lastStayServiceName : undefined,
        lastStayRange,
    ].filter(Boolean).join(' · ');

    const chips = [
        ...(submission.guestCategories ?? []),
        completedStays !== undefined ? `${t('Completed stays')}: ${completedStays}` : undefined,
        guestAge !== undefined ? `${t('Age')}: ${guestAge}` : undefined,
        submission.invitationId ? t('Invited') : t('Public link'),
    ].filter(Boolean) as string[];

    const contextRows: SurveySubmissionGuestContextRow[] = [];

    if (reservationId) {
        contextRows.push({
            kind: 'reservation',
            href: reservationHref,
            label: t('Booked'),
            title: bookedServiceName,
            detail: bookedDetail || undefined,
        });
    } else if (submission.sectorName) {
        contextRows.push({
            kind: 'context',
            label: t('Context'),
            title: submission.sectorName,
        });
    }

    upcomingStays.forEach((stay, index) => {
        const service = upcomingStayServices.find(item => item.id === stay.accommodationServiceId);
        const serviceName = localize(service?.nameLocalized) || service?.name;
        const title = stay.roomId ? `${t('Room')} ${stay.roomId}` : serviceName || t('Stay');
        const range = [
            formatLocalISODateToDMY(stay.startDay),
            formatLocalISODateToDMY(stay.endDay),
        ].filter(Boolean).join(' – ');
        const detail = [
            serviceName && serviceName !== title ? serviceName : undefined,
            range,
        ].filter(Boolean).join(' · ');

        contextRows.push({
            kind: 'upcoming',
            href: `${reservationsBaseHref}/${stay.reservationId}`,
            label: index === 0 ? t('Next stay') : t('Upcoming stay'),
            title,
            detail: detail || undefined,
        });
    });

    if (lastAccommodationStay?.reservationId && !lastStayMatchesBookedReservation) {
        contextRows.push({
            kind: 'stay',
            href: lastStayHref,
            label: t('Last stay'),
            title: lastStayTitle,
            detail: lastStayDetail || undefined,
        });
    }

    return {
        loading: enabled && (
            (!!profileId && profileQuery.isLoading) || (!!reservationId && reservationQuery.isLoading)
        ),
        name,
        initials,
        chips,
        preferences: hasRelevantPreferences ? {allergies, diets, medical} : undefined,
        contextRows,
        profileHref: profileId ? profileHref : undefined,
        profile,
        reservation,
    };
};
